import { useState } from "react";
import { useDispatch } from "react-redux";
import Loading from "../../../../components/loading";
import Error from "../../../../components/Error";
import { toast } from "react-toastify";
import { tokens } from "../../../../theme";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import ProductDetailsModal from "../../../../components/productDetailsModal";
import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { updatePositions } from "../../../../features/categorySlice";
import { useTheme } from "@emotion/react";
import NoDataTable from "../../../../components/noDataTable";
import { useRestaurant } from "../../../../hooks/RestaurantContext";
import SortableItem from "../../../../components/sortableItem";
import TableActions from "../../../../components/tableActions";
import { ellipsisText } from "../../../../global/functions/textFormat";
import ParagraphHighliter from "../../../../components/paragraphHighliter";
import { useTranslation } from "react-i18next";
const ListViewProducts = ({
  handleClickOpen,
  handleModify,
  handleDiscount,
  filteredProducts,
  setFilteredProducts,
  status,
  error,
  search,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { restaurantId, currency } = useRestaurant();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);

  const handleViewDetails = (product) => {
    setDetailsProduct(product);
    setDetailsOpen(true);
  };

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = filteredProducts.findIndex(
        (item) => item._id === active.id
      );
      const newIndex = filteredProducts.findIndex(
        (item) => item._id === over.id
      );
      const newOrder = arrayMove(filteredProducts, oldIndex, newIndex);
      if (oldIndex === newIndex) return;
      setFilteredProducts(newOrder);
      const positions = newOrder.map((item, index) => ({
        productId: item._id,
        position: index + 1,
      }));

      dispatch(
        updatePositions({
          categoryId: newOrder[0].category,
          positions: positions,
          restaurantId,
        })
      )
        .unwrap()
        .then(() => toast.success("Position modifiée avec succès"))
        .catch((error) => toast.error(error.message));
    }
  };

  return (
    <>
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      <TableContainer>
        <Table
          sx={{
            backgroundColor: colors.primary[400],
          }}
        >
          <TableHead
            sx={{
              backgroundColor: colors.primary[700],
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 60 }}>{t("table.number")}</TableCell>
              <TableCell sx={{ width: 90 }}>{t("table.image")}</TableCell>
              <TableCell sx={{ width: "18%", whiteSpace: "nowrap" }}>
                {t("table.name")}
              </TableCell>
              <TableCell sx={{ width: "30%" }}>
                {t("table.description")}
              </TableCell>
              <TableCell align="center" sx={{ width: "12%", whiteSpace: "nowrap" }}>
                {t("table.price")}
              </TableCell>
              <TableCell sx={{ width: "16%", whiteSpace: "nowrap" }}>
                {t("table.options")}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {t("table.visibility")}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {t("table.availability")}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loadingFetchData" ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Loading size={8} />
                </TableCell>
              </TableRow>
            ) : status === "fetchError" ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Error>{error}</Error>
                </TableCell>
              </TableRow>
            ) : (
              <SortableContext
                items={filteredProducts.map((item) => item._id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredProducts && filteredProducts.length > 0 ? (
                  filteredProducts.map((item, index) => {
                    const hasDiscount =
                      item.discountValue && item.discountValue > 0;
                    return (
                      <SortableItem
                        key={item._id}
                        id={item._id}
                        disableDragArea={[8]}
                      >
                        <>{index + 1}</>
                        <>
                          <img
                            src={`${process.env.REACT_APP_MEDIA_URL}/${item.image}`}
                            alt={item.name}
                            style={{
                              width: "75px",
                              height: "75px",
                              objectFit: "contain",
                            }}
                          />
                        </>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                          <ParagraphHighliter
                            title={item?.name}
                            search={search}
                          />
                        </Box>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                          <ParagraphHighliter
                            title={ellipsisText(item?.description, 60)}
                            search={search}
                          />
                        </Box>
                        <>
                          {item.discountValue && item.discountValue > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.25,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "lightcoral",
                                  fontWeight: "bold",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {item.originalPrice} {currency}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: "mediumseagreen",
                                  fontWeight: "bold",
                                }}
                              >
                                {item.discountedPrice
                                  ? item.discountedPrice.toFixed(2)
                                  : (
                                      item.originalPrice - item.discountValue
                                    ).toFixed(2)}{" "}
                                {currency}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              component="span"
                              sx={{ fontWeight: "bold", display: "block", textAlign: "center" }}
                            >
                              {item.price} {currency}
                            </Typography>
                          )}
                        </>
                          <Typography variant="inherit" component="div">
                            {item.type?.[0]?.name ?? "—"}
                            {item.type?.length > 1 && (
                              <Tooltip
                                title={
                                  <div>
                                    {item.type.slice(1).map((type) => (
                                      <div key={type._id}>{type.name}</div>
                                    ))}
                                  </div>
                                }
                                arrow
                              >
                                <Chip
                                  size="small"
                                  label={`+${item.type.length - 1}`}
                                  color="default"
                                  sx={{ ml: 0.5, borderRadius: "50%" }}
                                />
                              </Tooltip>
                            )}
                          </Typography>
                        <>
                          <Chip
                            variant="outlined"
                            label={item.visible ? "Visible" : "Caché"}
                            color={item.visible ? "success" : "error"}
                          />
                        </>
                        <>
                          <Chip
                            variant="outlined"
                            label={
                              item.outOfStock ? "Rupture de stock" : "En stock"
                            }
                            color={item.outOfStock ? "error" : "success"}
                          />
                        </>
                        <>
                          <TableActions
                            actions={[
                              {
                                type: "view",
                                color: "info",
                                label: t("common.viewDetails"),
                                onClick: () => handleViewDetails(item),
                              },
                              {
                                type: "delete",
                                color: "error",
                                label: t("common.delete"),
                                onClick: () => handleClickOpen(item._id),
                              },
                              {
                                type: "edit",
                                color: "warning",
                                label: t("common.edit"),
                                onClick: () => handleModify(item),
                              },
                              {
                                type: "discount",
                                color: hasDiscount ? "success" : "info",
                                label: t("common.discount"),
                                onClick: () => handleDiscount(item),
                              },
                            ]}
                          />
                        </>
                      </SortableItem>
                    );
                  })
                ) : (
                  <NoDataTable cols={9} />
                )}
              </SortableContext>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DndContext>
    <ProductDetailsModal
      open={detailsOpen}
      onClose={() => {
        setDetailsOpen(false);
        setDetailsProduct(null);
      }}
      product={detailsProduct}
      currency={currency}
      onEdit={() => {
        setDetailsOpen(false);
        handleModify(detailsProduct);
      }}
      onDelete={() => {
        setDetailsOpen(false);
        handleClickOpen(detailsProduct._id);
      }}
      onDiscount={() => {
        setDetailsOpen(false);
        handleDiscount(detailsProduct);
      }}
    />
    </>
  );
};

export default ListViewProducts;
